import { useEffect, useState } from "react";
import ThemeToggle from "./ThemeToggle";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const link =
    "text-[13px] tracking-[0.04em] text-[var(--text-80)] hover:text-[var(--color-primary)] transition-colors duration-300";

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl border-b border-[var(--border-subtle)]"
          : "bg-transparent"
      }`}
      style={scrolled ? { background: "var(--nav-bg)" } : undefined}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-[72px]">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-2.5 group"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <div
            className="flex-shrink-0 px-1.5 py-1"
            style={{
              background: "var(--logo-bg)",
              backdropFilter: "blur(14px) saturate(140%)",
              WebkitBackdropFilter: "blur(14px) saturate(140%)",
            }}
          >
            <img
              src="/sovereign-logo.png"
              alt="Sovereign Supercomputing"
              className="h-9 md:h-10 w-auto pointer-events-none block"
              style={{ mixBlendMode: "var(--logo-blend)" }}
            />
          </div>
          <div className="leading-tight">
            <div className="font-display text-[var(--text)] text-[15px] font-semibold tracking-tight">
              SOVEREIGN
            </div>
            <div className="font-mono text-[9px] tracking-[0.3em] text-[var(--color-accent-text)]">
              SUPERCOMPUTING
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          <button
            data-testid="nav-link-infrastructure"
            className={link}
            onClick={() => scrollTo("pillars")}
          >
            Infrastructure
          </button>
          <button
            data-testid="nav-link-impact"
            className={link}
            onClick={() => scrollTo("metrics")}
          >
            Impact
          </button>
          <button
            data-testid="nav-link-challenge"
            className={link}
            onClick={() => scrollTo("challenge")}
          >
            Challenge
          </button>
          <button
            data-testid="nav-link-solutions"
            className={link}
            onClick={() => scrollTo("solutions")}
          >
            Solutions
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <ThemeToggle />
          <button
            data-testid="nav-cta-contact"
            onClick={() => scrollTo("contact")}
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.12em] text-black bg-[var(--color-yellow)] px-5 py-2.5 hover:bg-[var(--color-yellow-deep)] transition-colors"
          >
            Join the mission
            <span aria-hidden>→</span>
          </button>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <ThemeToggle />
          <button
            data-testid="mobile-menu-toggle"
            className="text-[var(--text)]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <div className="w-6 flex flex-col gap-1.5">
              <span
                className={`h-px bg-current transition-transform ${open ? "translate-y-2 rotate-45" : ""}`}
              />
              <span
                className={`h-px bg-current transition-opacity ${open ? "opacity-0" : ""}`}
              />
              <span
                className={`h-px bg-current transition-transform ${open ? "-translate-y-1 -rotate-45" : ""}`}
              />
            </div>
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-[var(--border-subtle)] px-6 py-6 space-y-4"
          style={{ background: "var(--surface)" }}
        >
          <button
            className={`block w-full text-left ${link}`}
            onClick={() => scrollTo("pillars")}
          >
            Infrastructure
          </button>
          <button
            className={`block w-full text-left ${link}`}
            onClick={() => scrollTo("metrics")}
          >
            Impact
          </button>
          <button
            className={`block w-full text-left ${link}`}
            onClick={() => scrollTo("challenge")}
          >
            Challenge
          </button>
          <button
            className={`block w-full text-left ${link}`}
            onClick={() => scrollTo("solutions")}
          >
            Solutions
          </button>
          <button
            data-testid="mobile-cta-contact"
            onClick={() => scrollTo("contact")}
            className="w-full text-[12px] uppercase tracking-[0.12em] text-black bg-[var(--color-yellow)] px-5 py-3 mt-4"
          >
            Join the mission →
          </button>
        </div>
      )}
    </header>
  );
}
